// Media/Content Types based on actual API response

/**
 * Main content/media item interface
 */
export interface ContentItem {
    name: string;
    type: string;
    src: string;
    isFavorite?: boolean;
    /** Playlist content ID (nullable) */
    playlistContentId: string | null;

    /** Unique content identifier */
    contentId: string;

    /** Associated dealer ID */
    dealerId: string;

    /** Associated advertiser ID */
    advertiserId: string;

    /** Host ID */
    hostId: string;

    /** Original file name */
    fileName: string;

    /** Base URL for the file */
    url: string;

    /** File type/extension */
    fileType: string;

    /** File handler ID */
    handlerId: string;

    /** Date when content was created (ISO string) */
    dateCreated: string;

    /** UUID */
    uuid: string;

    /** Whether content is full screen (0 = false, 1 = true) */
    isFullScreen: 0 | 1;

    /** File size in bytes (as string) */
    filesize: string;

    /** Thumbnail URL */
    thumbnail: string;

    /** Preview thumbnail URL (nullable) */
    previewThumbnail: string | null;

    /** Sequence number */
    seq: number;

    /** Play frequency (nullable) */
    frequency: number | null;

    /** Parent content ID (nullable) */
    parentId: string | null;

    /** Duration in seconds */
    duration: number;

    /** Total times played */
    totalPlayed: number;

    /** Whether content is active (0 = inactive, 1 = active) */
    isActive: 0 | 1;

    /** Whether file is converted (0 = no, 1 = yes) */
    isConverted: 0 | 1;

    /** ID of user who created the content */
    createdBy: string;

    /** Name of user who created the content */
    createdByName: string;

    /** Owner type (nullable) */
    ownerType: string | null;

    /** Display title */
    title: string;

    /** Description (nullable) */
    description: string | null;

    /** Classification/category (nullable) */
    classification: string | null;

    /** Prefix (nullable) */
    prefix: string | null;

    /** Reference dealer ID (nullable) */
    refDealerId: string | null;

    /** Playlist contents schedule (nullable) */
    playlistContentsSchedule: any | null;

    /** Playlist content credits (nullable) */
    playlistContentCredits: any | null;

    /** Whether credits are enabled (nullable) */
    creditsEnabled: boolean | null;

    /** Total plays count */
    playsTotal: number;

    /** Total durations */
    durationsTotal: number;

    /** Associated feed ID (nullable) */
    feedId: string | null;

    /** Whether content is protected (0 = no, 1 = yes) */
    isProtected: 0 | 1;

    /** Owner role ID */
    ownerRoleId: number;

    /** Content plays list count (nullable) */
    contentPlaysListCount: number | null;

    /** Playlist content schedule (nullable) */
    playlistContentSchedule: any | null;

    /** Content category (0 = default) */
    contentCategory: number;

    /** Filler group name (nullable) */
    fillerGroupName: string | null;

    /** Filler group ID (nullable) */
    fillerGroupId: string | null;

    /** Admin only flag (nullable) */
    adminOnly: boolean | null;

    /** Filler playlist ID (nullable) */
    fillerPlaylistId: string | null;

    /** Filler playlist name (nullable) */
    fillerPlaylistName: string | null;

    /** Date when content was last updated (nullable) */
    dateUpdated: string | null;

    /** Whether content is migrated (0 = no, 1 = yes) */
    isMigrated: 0 | 1;

    /** Screen template zone playlists (nullable) */
    screenTemplateZonePlaylists: any | null;

    /** Template zones (nullable) */
    templateZones: any | null;

    /** Whether content is deleted */
    isDeleted: boolean;

    /** Reason for deletion (nullable) */
    deletedReason: string | null;
}

/**
 * Enum for owner role IDs
 */
export enum OwnerRoleId {
    DEALER = 1,
    ADMIN = 2,
    ADVERTISER = 3,
    HOST = 4,
}

/**
 * Enum for content categories
 */
export enum ContentCategory {
    DEFAULT = 0,
    VIDEO = 1,
    IMAGE = 2,
    FEED = 3,
    ADVERTISEMENT = 4,
}

/**
 * Supported file types
 */
export type FileType =
    | 'webm'
    | 'mp4'
    | 'mov'
    | 'avi'
    | 'jpg'
    | 'jpeg'
    | 'png'
    | 'gif'
    | 'webp'
    | 'svg';

/**
 * Binary flag type (used throughout the API)
 */
export type BinaryFlag = 0 | 1;

/**
 * Content creation request
 */
export interface CreateContentRequest {
    dealerId: string;
    advertiserId?: string;
    hostId: string;
    fileName: string;
    fileType: FileType;
    title: string;
    description?: string;
    isFullScreen?: BinaryFlag;
    ownerRoleId: OwnerRoleId;
    contentCategory?: ContentCategory;
    isActive?: BinaryFlag;
}

/**
 * Content update request
 */
export interface UpdateContentRequest {
    contentId: string;
    title?: string;
    description?: string;
    isFullScreen?: BinaryFlag;
    isActive?: BinaryFlag;
    seq?: number;
    frequency?: number;
    classification?: string;
    contentCategory?: ContentCategory;
}

/**
 * Content deletion request
 */
export interface DeleteContentRequest {
    contentId: string;
    deletedReason?: string;
    /** Soft delete (mark as deleted) vs hard delete */
    softDelete?: boolean;
}

/**
 * Content filter options
 */
export interface ContentFilters {
    dealerId?: string;
    advertiserId?: string;
    hostId?: string;
    createdBy?: string;
    fileType?: FileType | FileType[];
    ownerRoleId?: OwnerRoleId;
    contentCategory?: ContentCategory;
    isActive?: BinaryFlag;
    isDeleted?: boolean;
    isConverted?: BinaryFlag;
    isProtected?: BinaryFlag;
    feedId?: string;
    playlistId?: string;
    dateCreatedFrom?: string;
    dateCreatedTo?: string;
    searchQuery?: string;
}

/**
 * Content list response
 */
export interface ContentListResponse {
    success: boolean;
    data: ContentItem[];
    total: number;
    page: number;
    pageSize: number;
    message?: string;
}

/**
 * Single content response
 */
export interface ContentResponse {
    success: boolean;
    data: ContentItem;
    message?: string;
}

/**
 * Content statistics
 */
export interface ContentStatistics {
    totalContent: number;
    totalVideos: number;
    totalImages: number;
    totalDuration: number;
    totalFileSize: number;
    totalPlays: number;
    activeContent: number;
    inactiveContent: number;
    byFileType: Record<FileType, number>;
    byCategory: Record<ContentCategory, number>;
    byOwnerRole: Record<OwnerRoleId, number>;
}

/**
 * Playlist content schedule
 */
export interface PlaylistContentSchedule {
    scheduleId: string;
    playlistContentId: string;
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
    daysOfWeek?: number[];
    isActive: BinaryFlag;
}

/**
 * Playlist content credits
 */
export interface PlaylistContentCredits {
    creditId: string;
    playlistContentId: string;
    creditText: string;
    position: 'top' | 'bottom' | 'overlay';
    duration: number;
    fontSize?: number;
    fontColor?: string;
    backgroundColor?: string;
}

/**
 * Screen template zone
 */
export interface ScreenTemplateZone {
    zoneId: string;
    templateId: string;
    zoneName: string;
    x: number;
    y: number;
    width: number;
    height: number;
    zIndex: number;
    playlistId?: string;
}

/**
 * Filler group
 */
export interface FillerGroup {
    fillerGroupId: string;
    fillerGroupName: string;
    dealerId: string;
    description?: string;
    isActive: BinaryFlag;
    createdBy: string;
    dateCreated: string;
}

/**
 * Upload progress callback
 */
export interface UploadProgress {
    contentId: string;
    fileName: string;
    progress: number; // 0-100
    status: 'uploading' | 'converting' | 'complete' | 'error';
    message?: string;
}

/**
 * Batch content operation
 */
export interface BatchContentOperation {
    contentIds: string[];
    operation: 'activate' | 'deactivate' | 'delete' | 'protect' | 'unprotect';
    reason?: string;
}

/**
 * Batch operation response
 */
export interface BatchOperationResponse {
    success: boolean;
    totalProcessed: number;
    successCount: number;
    failedCount: number;
    results: Array<{
        contentId: string;
        success: boolean;
        message?: string;
    }>;
}

/**
 * Sort options for content list
 */
export interface ContentSortOptions {
    field: 'dateCreated' | 'title' | 'fileName' | 'duration' | 'filesize' | 'totalPlayed' | 'seq';
    order: 'asc' | 'desc';
}

/**
 * Content analytics
 */
export interface ContentAnalytics {
    contentId: string;
    views: number;
    uniqueViews: number;
    totalPlayTime: number;
    averageViewDuration: number;
    completionRate: number;
    viewsByDate: Array<{
        date: string;
        views: number;
    }>;
    viewsByDevice: Record<string, number>;
    viewsByLocation: Record<string, number>;
}

/**
 * Helper type for formatted file size
 */
export type FormattedFileSize = {
    bytes: number;
    formatted: string; // e.g., "1.37 MB"
    unit: 'B' | 'KB' | 'MB' | 'GB' | 'TB';
};

/**
 * Helper type for formatted duration
 */
export type FormattedDuration = {
    seconds: number;
    formatted: string; // e.g., "53s" or "0:53"
    minutes?: number;
    hours?: number;
};

/**
 * Utility functions interface
 */
export interface ContentHelpers {
    formatFileSize: (bytes: string | number) => FormattedFileSize;
    formatDuration: (seconds: number) => FormattedDuration;
    parseDate: (dateString: string) => Date;
    isVideo: (fileType: string) => boolean;
    isImage: (fileType: string) => boolean;
    getThumbnailUrl: (content: ContentItem) => string;
    getFullMediaUrl: (content: ContentItem) => string;
}
